import { useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { Spacer } from '../../components/Spacer';
import { Typography } from '../../components/Typography';
import { updateBookingAction } from '../../data/actions/bookingAction';
import { fetchObeeAvailabilitySuggestionsAction } from '../../data/actions/obeeAvailabilitySuggestionsAction';
import { freeBookingAction, holdBookingAction } from '../../data/actions/obeeBookingAction';
import { graphQLQuery } from '../../data/api/ApiClient';
import { availabilitySuggestions } from '../../data/graphql/queries';
import { bookingKeys, bookingPropTypes } from '../../data/models/Booking';
import { restaurantPropTypes } from '../../data/models/Restaurant';
import { isEmpty } from '../../utils/helpers';
import { useDidUpdateEffect } from '../../utils/hooks';

/**
 * Modal when the user's booking hold has timed out.
 * Tries to continue their booking with a fresh table matching the time, if possible
 *
 * @param open
 * @param booking
 * @param fetchObeeAvailabilitySuggestions
 * @param updateBooking
 * @returns {JSX.Element}
 * @constructor
 */
const TimedOut = ({
  open,
  onReset,
  booking,
  updateBooking,
  holdBooking,
  freeBooking,
  restaurant,
  fromEventWidget,
}) => {
  const [refreshingAvailability, setRefreshingAvailability] = useState(false);
  const loading = refreshingAvailability;

  const theme = useTheme();

  const startOver = () => {
    // Avoid edge case if both buttons clicked in quick succession
    if (loading) {
      return;
    }

    window.location.reload();
  };

  // Do a fresh availability check and see if there are any tables for the same time and area as before
  const refreshAvailability = async () => {
    // If no time selected, just reset
    if (!booking?.availabilitySuggestion?.time) {
      startOver();
      return;
    }

    setRefreshingAvailability(true);

    // Note: We're not using redux here because we don't want to refresh the list of availabilities shown to the user
    const response = await graphQLQuery(
      availabilitySuggestions,
      {
        areaId: booking.availabilitySuggestion.table.areaId,
        date: booking.date,
        times: [booking.availabilitySuggestion.time],
        size: booking.guests,
        length: 100,
        public: true, // public is for online booking / general availability
      },
      true,
    );

    setRefreshingAvailability(false);

    const suggestion = response.data.availabilitySuggestions.find(
      (availabilitySuggestion) =>
        availabilitySuggestion?.time === booking?.availabilitySuggestion.time,
    );

    // if preferred time availability suggestion exists let's set it for them
    if (suggestion) {
      updateBooking(bookingKeys.availabilitySuggestion, {
        ...suggestion,
        allAreas: booking.areaId === -1,
        waitlist: false,
      });

      // re-hold booking when continue booking
      // if (booking.heldBookingId) {
      // Hold the new booking
      // TODO optimise this to not need to import areas. e.g. use a helper
      holdBooking(
        booking,
        suggestion.table.areaId,
        suggestion.table.tableNumber,
        restaurant.data.areas.find((area) => area.id === suggestion.table.areaId)?.duration,
        fromEventWidget,
      );
      // }

      // If failed to hold the new booking, reset the process
      onReset();
    } else {
      startOver();
    }
  };

  // TODO make it so continuing re-holds the experiences and addons
  const hasAddonsOrExperiences =
    !isEmpty(booking?.selectedExperiences) || !isEmpty(booking?.addons);

  // Free the booking when this opens
  useDidUpdateEffect(() => {
    if (open && !hasAddonsOrExperiences) {
      freeBooking(booking?.heldBookingId);
    }
  }, [open, hasAddonsOrExperiences]);

  const handleClose = (_, reason) => {
    // Prevent closing the dialog when clicking the backdrop
    if (reason === 'backdropClick') {
      return;
    }
    if (hasAddonsOrExperiences) {
      startOver();
    } else {
      refreshAvailability();
    }
  };

  return (
    <Modal
      showHeader
      title='Session has timed out'
      open={open}
      onClose={handleClose}
      closeButtonStyle={{
        color: theme.colors.smoke,
      }}
    >
      <Spacer direction='vertical' gap={48} style={{ paddingTop: '20px' }}>
        <Typography>
          {hasAddonsOrExperiences
            ? 'Your session has timed out. Do you wish to start over?'
            : 'Your session has timed out. Do you still want to continue making your booking?'}
        </Typography>
        <Spacer style={{ alignItems: 'flex-end' }}>
          {!hasAddonsOrExperiences && (
            <Spacer direction='vertical'>
              <Typography style={{ opacity: loading ? 1 : 0, transition: 'opacity 0.5s' }}>
                Checking availability...
              </Typography>
              <Button size='small' color='primary' onClick={refreshAvailability} loading={loading}>
                Continue booking
              </Button>
            </Spacer>
          )}
          <Button
            variant='outlined'
            size='small'
            onClick={startOver}
            disabled={loading}
            style={{
              borderRadius: `${theme.colors.availabilityMainButtonCornerRadius || 2}px`,
              color: theme.colors.smoke,
              border: `1px solid ${theme.colors.smoke}`,
            }} // TODO get from restaurant settings
          >
            Start over
          </Button>
        </Spacer>
      </Spacer>
    </Modal>
  );
};

TimedOut.defaultProps = {
  open: false,
  fromEventWidget: false,
};

TimedOut.propTypes = {
  booking: bookingPropTypes.isRequired,
  updateBooking: PropTypes.func.isRequired,
  open: PropTypes.bool,
  onReset: PropTypes.func.isRequired,
  holdBooking: PropTypes.func.isRequired,
  freeBooking: PropTypes.func.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  fromEventWidget: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  restaurant: state.restaurant,
  booking: state.booking,
  obeeAvailabilitySuggestions: state.obeeAvailabilitySuggestions,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchObeeAvailabilitySuggestions: fetchObeeAvailabilitySuggestionsAction,
      updateBooking: updateBookingAction,
      holdBooking: holdBookingAction,
      freeBooking: freeBookingAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(TimedOut);
